import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import * as cciApi from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import {getHHmmFormat, getYYYYMMddFormat} from '@di-shared/api/di/utils';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {DemeritEducationReservationFormType} from '@di-shared/features/public-service/demerit-education/DemeritEducationReservationForm';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

interface DemeritEducationReservationStep5TimeProps {
  vin: string;
  form: DemeritEducationReservationFormType;
  onUpdateForm(partial: Partial<DemeritEducationReservationFormType>): void;
}

function DemeritEducationReservationStep5Time(props: DemeritEducationReservationStep5TimeProps) {
  const {t} = useInferableTranslation();
  const {backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [handleError] = usePendingHandler(async () => {
    await openAlert({
      title: t('T10314'),
      message: t('T10328'),
      okText: t('T10221'),
    });
    backOrClose();
  });

  return (
    <ErrorBoundary
      fallbackRender={() => null}
      onError={handleError}
    >
      <Suspense
        fallback={
          <div className="wrap">
            <Header left={<HeaderBackButton />}>
              <h1 className="headline_xxsmall nav_center_title">{t('T10348')}</h1>
            </Header>
            <main className="content">
              <section className="section section_inspection">
                <div className="inspection_info">
                  <p className="place_text">
                    <span className="ic ic_basic_location_16px" />
                    <span className={clsx('region', {is_select: props.form.branchName})}>{props.form.branchName || t('T10272')}</span>
                    <span className={clsx('location', {is_select: props.form.centerName})}>{props.form.centerName || t('T10261')}</span>
                  </p>
                  <p
                    className={clsx('inspection_text', {
                      is_select:
                        (props.form.educationStartDate && props.form.educationEndDate) ||
                        (props.form.educationStartTime && props.form.educationEndTime),
                    })}
                  >
                    <span className="ic ic_calendar_16px" />
                    <span className={clsx('date', {is_select: props.form.educationStartDate && props.form.educationEndDate})}>
                      {props.form.educationStartDate && props.form.educationEndDate
                        ? `${CldrDatetimeFormatter.format(props.form.educationStartDate, 'Md')} - ${CldrDatetimeFormatter.format(
                            props.form.educationEndDate,
                            'Md'
                          )}`
                        : t('T10320')}
                    </span>
                    <span className={clsx('time', {is_select: props.form.educationStartTime && props.form.educationEndTime})}>
                      {props.form.educationStartTime && props.form.educationEndTime
                        ? `${CldrDatetimeFormatter.format(props.form.educationStartTime, 'aHM')} - ${CldrDatetimeFormatter.format(
                            props.form.educationEndTime,
                            'aHM'
                          )}`
                        : t('T10276')}
                    </span>
                  </p>
                </div>
                <div className="place_select_area">
                  {['강의실1', '강의실2', '강의실3']?.map((classroom) => (
                    <div
                      className="input_box input_box_outline"
                      key={classroom}
                    >
                      <input
                        type="radio"
                        name="classroom"
                        className="input"
                      />
                      <label className="label">
                        <div className="place_info">
                          <span className="place_name skeleton">{classroom}</span>
                          <p className="date skeleton">
                            {t('T10275')} : {CldrDatetimeFormatter.format(new Date(), 'yMMMEd')}
                          </p>
                          <span className="time skeleton">
                            {t('T10276')} : {CldrDatetimeFormatter.format(new Date(), 'aHM')} -{' '}
                            {CldrDatetimeFormatter.format(new Date(), 'aHM')}
                          </span>
                        </div>
                        <span className="status skeleton">({t('T10278')} 20/40)</span>
                      </label>
                    </div>
                  ))}
                </div>
              </section>
            </main>
            <div className="floating_action_area">
              <div className="button_box_complex">
                <button
                  type="button"
                  className="btn btn_large btn_outline btn_full"
                >
                  {t('T10316')}
                </button>
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  disabled
                >
                  {t('T10322')}
                </button>
              </div>
            </div>
          </div>
        }
      >
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, form, onUpdateForm}: DemeritEducationReservationStep5TimeProps) {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const puid = environmentStore.getState().puid;
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const centerParams = {
    branchCode: form.branchCode,
    eduStartDate: form.educationStartDate ? getYYYYMMddFormat(form.educationStartDate) : '',
    eduEndDate: form.educationEndDate ? getYYYYMMddFormat(form.educationEndDate) : '',
  };
  const {data: classroomData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.demeritEducationController.getCenters), centerParams],
    queryFn: async () => {
      // 정상 조회 완료(B00) 이외에는 에러로 취급
      // B00 정상 | C95 연계 오류 | C96 암복호화에러 | C97 파라메터 없음 | C98 필수 파라메터 값 없음 | C99 시스템 오류
      // https://hmg.dooray.com/project/tasks/4117484859229622126
      const response = await diApi.demeritEducationController.getCenters(centerParams);
      if (response.data.resultCode !== 'B00') {
        throw new Error('classroom data not available');
      }
      return response;
    },
    select: ({data}) => data.contents.find((content) => content.code === form.centerCode)?.classroomList,
  });

  const [updateDemeritEducationReservation] = usePendingHandler(
    async () => {
      const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl(
        ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_SUCCESS.getFullPath(),
        {
          centerName: form.centerName,
          eduDate: CldrDatetimeFormatter.format(form.educationDate as Date, 'yMMMd'),
          eduTime: `${CldrDatetimeFormatter.format(form.educationStartTime as Date, 'aHM')} ~ ${CldrDatetimeFormatter.format(
            form.educationEndTime as Date,
            'aHM'
          )}`,
          centerNotice: form.centerNotice,
        }
      );
      if (isConfirm) {
        const meData = await cciApi.getMe();
        await diApi.demeritEducationController.createReserve({
          requestBody: {
            puid,
            eduDate: getYYYYMMddFormat(form.educationDate as Date),
            center: form.centerCode,
            classroomCode: form.classroomCode,
            startTime: getHHmmFormat(form.educationStartTime as Date),
            reserveName: meData.name,
            telephone: meData.phone,
          },
        });
        WebviewUtils.showToast(t('T10329'));
        ccWebviewUtils.closeContent();
      }
    },
    async () => {
      await ccWebviewUtils.openBottomSheetWebViewUrl(
        ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_DEMERIT_EDUCATION_RESERVATION_FAIL.getFullPath()
      );
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10348')}</h1>
      </Header>
      <main className="content">
        <section className="section section_inspection">
          <div className="inspection_info">
            <p className="place_text">
              <span className="ic ic_basic_location_16px" />
              <span className={clsx('region', {is_select: form.branchName})}>{form.branchName || t('T10272')}</span>
              <span className={clsx('location', {is_select: form.centerName})}>{form.centerName || t('T10261')}</span>
            </p>
            <p
              className={clsx('inspection_text', {
                is_select: (form.educationStartDate && form.educationEndDate) || (form.educationStartTime && form.educationEndTime),
              })}
            >
              <span className="ic ic_calendar_16px" />
              <span className={clsx('date', {is_select: form.educationStartDate && form.educationEndDate})}>
                {form.educationStartDate && form.educationEndDate
                  ? `${CldrDatetimeFormatter.format(form.educationStartDate, 'Md')} - ${CldrDatetimeFormatter.format(
                      form.educationEndDate,
                      'Md'
                    )}`
                  : t('T10320')}
              </span>
              <span className={clsx('time', {is_select: form.educationStartTime && form.educationEndTime})}>
                {form.educationStartTime && form.educationEndTime
                  ? `${CldrDatetimeFormatter.format(form.educationStartTime, 'aHM')} - ${CldrDatetimeFormatter.format(
                      form.educationEndTime,
                      'aHM'
                    )}`
                  : t('T10276')}
              </span>
            </p>
          </div>
          <div className="place_select_area">
            {classroomData?.map((classroom) => {
              const educationDate = parseDateWithFormat(classroom.eduDate);
              const startTime = parseDateWithFormat(classroom.eduDate + classroom.startTime);
              const endTime = parseDateWithFormat(classroom.eduDate + classroom.endTime);
              if (!educationDate || !startTime || !endTime) {
                throw new Error('demerit education date error');
              }
              const classroomId = `${classroom.classroomCode}-${classroom.eduDate}-${classroom.startTime}`;

              return (
                <div
                  className="input_box input_box_outline"
                  key={classroomId}
                >
                  <input
                    type="radio"
                    id={classroomId}
                    name="classroom"
                    className="input"
                    checked={classroom.classroomCode === form.classroomCode && startTime.getTime() === form.educationStartTime?.getTime()}
                    onChange={() => {
                      onUpdateForm({
                        classroomCode: classroom.classroomCode,
                        educationDate,
                        educationStartTime: startTime,
                        educationEndTime: endTime,
                      });
                    }}
                  />
                  <label
                    htmlFor={classroomId}
                    className="label"
                  >
                    <div className="place_info">
                      <span className="place_name">{classroom.classroomName}</span>
                      <p className="date">
                        {t('T10275')} : {CldrDatetimeFormatter.format(educationDate, 'yMMMEd')}
                      </p>
                      <span className="time">
                        {t('T10276')} : {CldrDatetimeFormatter.format(startTime, 'aHM')} - {CldrDatetimeFormatter.format(endTime, 'aHM')}
                      </span>
                    </div>
                    <span className="status">
                      ({t('T10278')} {classroom.reserveCapa}/{classroom.classCapa})
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={backOrClose}
          >
            {t('T10316')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!(form.classroomCode && form.educationDate && form.educationStartTime && form.educationEndTime)}
            onClick={updateDemeritEducationReservation}
          >
            {t('T10322')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemeritEducationReservationStep5Time;
